'use client';

import { useMemo } from 'react';
import {
  CheckIcon,
  CreditCardIcon,
  HouseLineIcon,
  TrendUpIcon,
  WalletIcon,
} from '@phosphor-icons/react';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getBankColor, getBankSurfaceColor } from '@/lib/bank-colors';
import { cardBrandImage, normalizeCardBrand } from '@/lib/card-brands';
import { cn } from '@/lib/utils';
import type { AccountKind, AccountOption } from './types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string | null;
  accounts: AccountOption[];
  onSelect: (value: string | null) => void;
}

const TYPE_ICON: Record<AccountKind, React.ComponentType<{ className?: string; weight?: 'duotone' }>> = {
  credit: CreditCardIcon,
  checking: WalletIcon,
  investment: TrendUpIcon,
};

const TYPE_LABEL: Record<AccountKind, string> = {
  credit: 'Crédito',
  checking: 'Conta',
  investment: 'Investimento',
};

interface BankGroup {
  bank: string;
  color: string;
  image: string | null;
  accounts: AccountOption[];
}

function groupByBank(accounts: AccountOption[]): BankGroup[] {
  const groups = new Map<string, BankGroup>();
  for (const a of accounts) {
    let group = groups.get(a.bank);
    if (!group) {
      group = {
        bank: a.bank,
        color: getBankColor(a),
        image: a.connectorImage ?? null,
        accounts: [],
      };
      groups.set(a.bank, group);
    } else if (!group.image && a.connectorImage) {
      group.image = a.connectorImage;
    }
    group.accounts.push(a);
  }
  return Array.from(groups.values());
}

export function AccountPickerDrawer({ open, onOpenChange, value, accounts, onSelect }: Props) {
  const groups = useMemo(() => groupByBank(accounts), [accounts]);
  const selected = value ?? '';

  const handleSelect = (next: string) => {
    onSelect(next === '' ? null : next);
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="bg-surface px-0 data-[vaul-drawer-direction=right]:h-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="flex flex-row items-center justify-between px-4 py-3">
          <DrawerTitle className="text-lg font-semibold text-text-primary">Conta</DrawerTitle>
          <DrawerDescription className="sr-only">
            Selecione uma conta para filtrar os lançamentos.
          </DrawerDescription>
          {value && (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={() => handleSelect('')}
            >
              Limpar
            </button>
          )}
        </DrawerHeader>

        <Command className="flex-1 overflow-hidden">
          <CommandInput placeholder="Buscar conta ou banco…" />
          <CommandList className="max-h-none flex-1">
            <CommandEmpty>Nenhuma conta encontrada.</CommandEmpty>

            <CommandGroup>
              <CommandItem
                value="todas as contas"
                onSelect={() => handleSelect('')}
                className="gap-3"
              >
                <span className="flex size-9 items-center justify-center rounded-xl bg-surface-sunken">
                  <HouseLineIcon className="size-4 text-text-tertiary" weight="duotone" />
                </span>
                <span className="flex-1 text-sm font-medium">Todas as contas</span>
                {selected === '' && <CheckIcon className="size-4 text-foreground" weight="bold" />}
              </CommandItem>
            </CommandGroup>

            {groups.map((group) => (
              <CommandGroup
                key={group.bank}
                heading={
                  <span className="flex items-center gap-2">
                    {group.image ? (
                      <img
                        src={group.image}
                        alt=""
                        className="size-4 shrink-0 rounded-full object-cover"
                        style={{ backgroundColor: `${group.color}1A` }}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ backgroundColor: group.color }}
                        aria-hidden
                      />
                    )}
                    <span className="text-sm font-semibold text-text-primary">{group.bank}</span>
                    <span className="text-2xs font-normal text-muted-foreground">
                      · {group.accounts.length} {group.accounts.length === 1 ? 'conta' : 'contas'}
                    </span>
                  </span>
                }
              >
                {group.accounts.map((account) => {
                  const Icon = TYPE_ICON[account.type];
                  const tinted = getBankSurfaceColor(group.color, 'tinted');
                  const brand = account.type === 'credit' ? normalizeCardBrand(account.creditBrand) : null;
                  const isSelected = selected === account.id;
                  // Searchable haystack: bank + type + label + last4
                  const searchValue = [
                    group.bank,
                    TYPE_LABEL[account.type],
                    account.label,
                    account.last4,
                    brand,
                  ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                  return (
                    <CommandItem
                      key={account.id}
                      value={searchValue}
                      onSelect={() => handleSelect(account.id)}
                      className="gap-3"
                    >
                      <span
                        className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                        style={{ backgroundColor: brand ? '#FFFFFF' : tinted }}
                      >
                        {brand ? (
                          <img
                            src={cardBrandImage(brand)}
                            alt={brand}
                            className="size-6 object-contain"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <Icon className="size-4" weight="duotone" />
                        )}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{TYPE_LABEL[account.type]}</span>
                          {account.label && (
                            <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-2xs font-bold tracking-wide text-text-secondary uppercase">
                              {account.label}
                            </span>
                          )}
                        </div>
                        {account.last4 && (
                          <span className="font-mono text-2xs text-muted-foreground tabular-nums">
                            ····{account.last4}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <CheckIcon className={cn('size-4 text-foreground')} weight="bold" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DrawerContent>
    </Drawer>
  );
}
